import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import * as Toast from '@radix-ui/react-toast';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CloseButton } from '#src/atoms/buttons/CloseButton/index.tsx';
import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { ToastBase } from '#src/atoms/snackbar/ToastBase/index.tsx';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';
import { thinMaterial } from '#src/style/utils.tsx';

type Props = {
  onClose: () => void;
  message: ReactNode;
  action?: string;
  open: boolean;
  autoHideDuration?: number | null;
};

const Root = styled(ToastBase)`
  display: flex;
  align-items: flex-start;
  max-width: 100%;
  margin: auto;
  ${thinMaterial}
  color: var(--c-white);
  padding: var(--double-unit);
  width: max-content;
  word-break: break-word;
  border: 1px solid var(--c-nd-150);
  gap: var(--unit);
  border-radius: var(--unit);
  a {
    text-decoration: underline;
  }
`;

const Close = styled(Toast.Close)`
  display: none;
  @media ${laptopAndAbove} {
    display: block;
  }
`;
const Description = styled(Toast.Description)`
  align-self: center;
  text-align: center;
  @media ${laptopAndAbove} {
    text-align: left;
  }
`;

export const Snackbar = ({
  onClose,
  open: defaultOpen,
  action,
  message,
  autoHideDuration = 5000,
}: Props) => {
  const [open, setOpen] = useState<boolean>();
  const { formatMessage } = useIntl();
  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) {
      /** timeout to prevent the message from disappearing too early and trigger a glitch during the slideIn animation */
      setTimeout(onClose, 100);
    }
  };

  if (typeof open === 'undefined' && defaultOpen) {
    setOpen(defaultOpen);
  } else if (typeof open === 'boolean' && !defaultOpen) {
    /** reset to undefined to be able to open multiple time, otherwise only 1 notification could be opened per session */
    setOpen(undefined);
  }

  return (
    <Root
      duration={autoHideDuration || Infinity}
      open={Boolean(open)}
      onOpenChange={onOpenChange}
    >
      <Description>{message}</Description>
      {!!action && (
        <Toast.Action
          altText={formatMessage({
            id: 'SnackBar.Action.altText',
            defaultMessage: 'Open external link',
          })}
          asChild
        >
          <IconButton
            size="medium"
            href={action}
            rel="noopener noreferrer"
            color="tertiary"
          >
            <FontAwesomeIcon icon={faSearch} />
          </IconButton>
        </Toast.Action>
      )}
      <Close asChild>
        <CloseButton size="medium" onClose={() => onOpenChange(false)} />
      </Close>
    </Root>
  );
};
